import { ArrowRight, CheckCircle2, Clock3, ShieldCheck, Smartphone, UsersRound } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const implementationItems = [
  "React on Cloudflare Pages",
  "TailwindCSS theme foundation",
  "shadcn/ui component base",
  "Worker, D1 and Durable Objects bootstrap"
];

const routeCards = [
  {
    path: "/admin",
    title: "Administration",
    description: "A calm control room for conventions, delegates, presence, tokens and archived results.",
    icon: UsersRound
  },
  {
    path: "/vote/:sessionCode/:token",
    title: "Voter Access",
    description: "A direct mobile-first link for eligible delegates, with no account creation required.",
    icon: Smartphone
  },
  {
    path: "/results/:sessionCode",
    title: "Public Result",
    description: "A minimal final view that never exposes detailed ballots to voters.",
    icon: ShieldCheck
  }
];

export function HomePage() {
  return (
    <main className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,oklch(0.86_0.1_83.2_/_0.32),transparent_34%),linear-gradient(135deg,oklch(0.985_0.018_96.8),oklch(0.92_0.035_157.8))] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 sm:gap-6 lg:py-10">
        <section
          className="relative overflow-hidden rounded-[2rem] border border-border/80 bg-card/80 p-6 shadow-2xl shadow-foreground/10 backdrop-blur sm:p-10 lg:p-14"
          aria-labelledby="hero-title"
        >
          <div className="absolute right-0 top-0 hidden size-72 translate-x-20 -translate-y-20 rounded-full bg-accent/30 blur-3xl sm:block" />
          <div className="relative max-w-4xl">
            <Badge variant="outline" className="mb-5 bg-background/70">
              PollCrafter Convention
            </Badge>
            <h1
              id="hero-title"
              className="font-serif text-4xl font-semibold leading-[0.95] tracking-[-0.055em] text-foreground sm:text-6xl lg:text-7xl"
            >
              Confidential voting for convention officers.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
              Built for presence-based eligibility, anonymous ballots, clear administration and a voting
              experience that stays simple on both mobile and desktop.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" asChild>
                <a href="/admin">
                  Open admin area
                  <ArrowRight aria-hidden="true" />
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/vote/demo-session/demo-token">Preview voter flow</a>
              </Button>
            </div>
          </div>
        </section>

        <Alert className="border-primary/20 bg-card/80 shadow-sm">
          <Clock3 className="absolute left-4 top-4 size-4 text-primary" aria-hidden="true" />
          <div className="pl-7">
            <AlertTitle>Architecture-first implementation</AlertTitle>
            <AlertDescription>
              Progress is tracked in <code>plan.md</code>. Each completed step must be verified before its checkbox
              is marked done.
            </AlertDescription>
          </div>
        </Alert>

        <section className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]" aria-label="Implementation status">
          <Card className="bg-card/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Implementation Status</CardTitle>
              <CardDescription>Current foundations verified before feature work continues.</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="grid gap-3">
                {implementationItems.map((item) => (
                  <li key={item} className="flex items-center gap-3 rounded-2xl bg-secondary/70 px-4 py-3 text-sm font-medium">
                    <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <section className="grid gap-5 sm:grid-cols-3" aria-label="Planned application areas">
            {routeCards.map((route) => {
              const Icon = route.icon;

              return (
                <Card key={route.path} className="bg-card/80 backdrop-blur">
                  <CardHeader>
                    <div className="mb-3 flex size-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                      <Icon className="size-5" aria-hidden="true" />
                    </div>
                    <Badge variant="secondary" className="w-fit font-mono text-[0.68rem]">
                      {route.path}
                    </Badge>
                    <CardTitle>{route.title}</CardTitle>
                    <CardDescription>{route.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </section>
        </section>
      </div>
    </main>
  );
}
